import { createTheme } from '@material-ui/core/styles';
import { createTheme as createThemeV5 } from '@mui/material/styles';
import { resolveBrandFontFamily } from './brandingAssets';

// What the UI ACTUALLY looks like today, before any branding is applied. These
// back the Appearance pickers' "Default" preview and the --brand-* CSS variable
// fallbacks, so an un-customised workspace shows the real current colors (the DQ
// orange accent, etc.) instead of a value that doesn't match the live UI.
export const DEFAULT_BRAND_COLORS = {
    primary: '#FF6900',     // brand accent: sidebar active, primary buttons, highlights
    secondary: '#052F44',   // deep navy used for headings / secondary actions
    text: '#111827',        // default body / heading text
    background: '#FFFBF7',  // app content background
};

// MUI palette defaults — these MIRROR src/theme.js so a workspace with NO branding
// overrides renders the MUI theme byte-for-byte the same as before this feature.
// (MUI's "primary" is the navy, distinct from the orange brand accent above.)
const MUI_THEME_DEFAULTS = {
    primary: '#FF6900',
    secondary: '#0097B2',
    text: '#052F44',
    background: '#F4EEEE',
};

// Dark-mode surface colors. The brand ACCENT colors (primary/secondary) are kept
// as-is so a workspace's identity survives the switch; only the surfaces (app
// background, cards/paper) and body text flip. These values also back the
// --brand-bg / --brand-text CSS variable overrides in the branding context.
export const DEFAULT_GLOBAL_COLORS = {
    global_1: '#FF6900',
    global_2: '#7B8FC7',
    global_3: '#0097B2',
};

export const DARK_SURFACE = {
    background: '#121212', // app content background
    paper: '#1E1E1E',      // cards / menus / dialogs
    text: '#E5E7EB',       // default body / heading text
    textSecondary: '#9CA3AF',
};

// Dark-mode tints for tinted boxes (light-orange cards, grey tags, warning boxes) whose
// light-mode colours are fixed hex values. Spread into a page root's sx in dark mode only;
// each use keeps its light hex as the fallback, e.g. var(--tint-soft, #FFF1E4).
export const DARK_TINT_VARS = {
    '--tint-soft': 'rgba(255,105,0,0.14)',          // light-orange box
    '--tint-soft-2': 'rgba(255,105,0,0.10)',        // lighter orange (selected option)
    '--tint-accent-text': '#FFB27A',                // dark-orange text on an orange box
    '--tint-accent-border': 'rgba(255,105,0,0.5)',
    '--tint-chip-bg': 'rgba(255,105,0,0.16)',
    '--tint-off-bg': 'rgba(255,255,255,0.04)',      // grey "not needed" box
    '--tint-off-border': 'rgba(255,255,255,0.12)',
    '--tint-warn-bg': 'rgba(255,105,0,0.10)',
    '--tint-warn-border': 'rgba(255,105,0,0.4)',
    '--tint-tag-bg': 'rgba(255,255,255,0.12)',      // grey tag ("Optional")
    '--tint-warm-border': 'rgba(255,255,255,0.14)', // beige card border
    '--tint-divider': 'rgba(255,255,255,0.1)',
    '--tint-track': 'rgba(255,255,255,0.15)',       // empty progress track
};

/**
 * Build the MUI theme from a workspace branding object. Any missing/null color
 * falls back to the DQ default, so partial branding is always safe.
 *
 * @param {{ colors?: { primary, secondary, text, background } }} [branding]
 * @param {'light' | 'dark'} [mode]  Active color mode (defaults to light).
 */
export const buildTheme = (branding, mode = 'light') => {
    const isDark = mode === 'dark';
    const c = (branding && branding.colors) || {};
    const primary = c.primary || MUI_THEME_DEFAULTS.primary;
    const secondary = c.secondary || MUI_THEME_DEFAULTS.secondary;
    const text = isDark ? DARK_SURFACE.text : (c.text || MUI_THEME_DEFAULTS.text);
    const background = isDark ? DARK_SURFACE.background : (c.background || MUI_THEME_DEFAULTS.background);
    const paper = isDark ? DARK_SURFACE.paper : '#F4F4F4';

    // Primary font (when set) applies across all MUI components via typography.
    const fontPrimary = resolveBrandFontFamily(
        branding && branding.fonts && branding.fonts.primary,
        'BrandPrimaryFont',
    );

    return createTheme({
        palette: {
            // Drives MUI's built-in dark adjustments (default text/dividers/etc.)
            // for components that don't hardcode their colors.
            type: isDark ? 'dark' : 'light',
            primary: { main: primary },
            secondary: { main: secondary },
            error: { main: '#f44336' },
            warning: { main: '#ff9800' },
            info: { main: '#2196f3' },
            success: { main: '#4caf50' },
            loginPage: { text: '#023F5F', background: '#A0D0D1' },
            text: { primary: text, secondary: isDark ? DARK_SURFACE.textSecondary : '#FF6900' },
            background: { default: background, paper, cards: paper },
        },
        ...(fontPrimary ? { typography: { fontFamily: fontPrimary } } : {}),
    });
};

/**
 * MUI v5 theme — mirrors the v4 palette so both component libraries
 * render consistently. `palette.mode` drives automatic dark adjustments
 * across ALL @mui/material components (Typography, Card, Dialog, etc.)
 * without needing to touch individual sx props.
 */
export const buildThemeV5 = (branding, mode = 'light') => {
    const isDark = mode === 'dark';
    const c = (branding && branding.colors) || {};
    const primary = c.primary || DEFAULT_BRAND_COLORS.primary;
    const secondary = c.secondary || DEFAULT_BRAND_COLORS.secondary;
    const fontPrimary = resolveBrandFontFamily(
        branding && branding.fonts && branding.fonts.primary,
        'BrandPrimaryFont',
    );

    return createThemeV5({
        palette: {
            mode: isDark ? 'dark' : 'light',
            primary:    { main: primary },
            secondary:  { main: secondary },
            error:      { main: '#f44336' },
            warning:    { main: '#ff9800' },
            info:       { main: '#2196f3' },
            success:    { main: '#4caf50' },
            text: {
                primary:   isDark ? DARK_SURFACE.text          : (c.text || '#111827'),
                secondary: isDark ? DARK_SURFACE.textSecondary : '#6B7280',
            },
            background: {
                default: isDark ? DARK_SURFACE.background : (c.background || '#FFFBF7'),
                paper:   isDark ? DARK_SURFACE.paper      : '#ffffff',
            },
        },
        // Only override the font when branding provides one — otherwise MUI's default.
        ...(fontPrimary ? { typography: { fontFamily: fontPrimary } } : {}),
    });
};

export default buildTheme;
